import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, MessageCircle, User } from 'lucide-react';
import { API, getHeaders, useBrand } from '../portalShared';

/**
 * Real-time chat with the brand's Freddy Media Campaign Manager.
 * Bootstrap with REST for history, then subscribe to /ws/portal/messages
 * for live updates. Falls back gracefully if the WS refuses to connect
 * (we still work as a poll-free single-page inbox).
 */
export default function Messages() {
  const brand = useBrand();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [wsState, setWsState] = useState('connecting'); // connecting | live | offline
  const [cm, setCm] = useState(null);
  const listRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  }, []);

  // Fetch history + CM
  useEffect(() => {
    axios.get(`${API}/portal/messages`, { headers: getHeaders() })
      .then(r => { setMessages(r.data.messages || []); scrollToBottom(); })
      .catch(() => {});
    axios.get(`${API}/portal/campaign-manager`, { headers: getHeaders() })
      .then(r => setCm(r.data.campaign_manager))
      .catch(() => {});
  }, [scrollToBottom]);

  // WebSocket connection
  useEffect(() => {
    const token = localStorage.getItem('propost_brand_token');
    if (!token) return;

    const connect = () => {
      // API is https://…/api → wss://…/api
      const wsBase = API.replace(/^http/, 'ws');
      const url = `${wsBase}/ws/portal/messages?token=${encodeURIComponent(token)}`;
      let ws;
      try {
        ws = new WebSocket(url);
      } catch {
        setWsState('offline');
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => setWsState('live');
      ws.onmessage = (ev) => {
        try {
          const parsed = JSON.parse(ev.data);
          if (parsed.type === 'message' && parsed.data) {
            setMessages(prev => {
              if (prev.some(m => m.id === parsed.data.id)) return prev;
              return [...prev, parsed.data];
            });
            scrollToBottom();
          }
        } catch { /* malformed frame — ignore */ }
      };
      ws.onclose = () => {
        // Only flip to offline if the socket never opened successfully.
        // Deliberate close from unmount would have wiped wsRef.current.
        if (wsRef.current === ws) setWsState('offline');
        // reconnect after 3s
        if (reconnectRef.current) clearTimeout(reconnectRef.current);
        reconnectRef.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => {
        setWsState('offline');
        try { ws.close(); } catch { /* already closed */ }
      };
    };
    connect();
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch { /* already closed */ } }
    };
  }, [scrollToBottom]);

  const send = async (e) => {
    e?.preventDefault?.();
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/portal/messages`, { text: trimmed }, { headers: getHeaders() });
      setMessages(prev => (prev.some(m => m.id === r.data.id) ? prev : [...prev, r.data]));
      setText('');
      scrollToBottom();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const initials = (name) => (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="max-w-3xl" data-testid="portal-messages">
      {/* CM header */}
      <div className="bg-[#111827] border border-white/10 p-4 flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#FF4343]/15 border border-[#FF4343]/40 flex items-center justify-center text-[#FF4343] font-mono text-sm font-bold" data-testid="cm-avatar">
            {cm?.avatar_url ? <img src={cm.avatar_url} alt="" className="w-full h-full rounded-full object-cover" /> : initials(cm?.name || 'Jon Fredrickson')}
          </div>
          <div>
            <p className="text-sm font-semibold text-white" data-testid="cm-name">{cm?.name || 'Jon Fredrickson'}</p>
            <p className="text-[10px] text-[#9CA3AF] font-mono">Your Freddy Media Campaign Manager</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase" data-testid="ws-state">
          <span className={`w-2 h-2 rounded-full ${wsState === 'live' ? 'bg-[#10B981] animate-pulse' : wsState === 'connecting' ? 'bg-yellow-400' : 'bg-[#6B7280]'}`} />
          <span className="text-[#9CA3AF]">{wsState === 'live' ? 'Live' : wsState === 'connecting' ? 'Connecting' : 'Offline'}</span>
        </div>
      </div>

      {/* Message list */}
      <div ref={listRef} data-testid="messages-list"
        className="bg-[#111827] border border-white/10 border-b-0 h-[420px] overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <MessageCircle className="w-8 h-8 text-[#6B7280] mb-2" />
            <p className="text-sm text-[#9CA3AF]">Say hi to your campaign manager.</p>
            <p className="text-xs text-[#6B7280] mt-1">Messages are seen instantly and also emailed to their inbox.</p>
          </div>
        ) : messages.map(m => {
          const mine = m.sender_type === 'brand';
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.id}`}>
              <div className={`max-w-[75%] ${mine ? 'items-end' : 'items-start'} flex flex-col`}>
                <span className="text-[9px] font-mono uppercase text-[#6B7280] mb-1 px-1">
                  {mine ? 'You' : (m.sender_name || 'Freddy Media')} · {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
                <div className={`px-3 py-2 text-sm whitespace-pre-wrap break-words ${mine ? 'bg-[#FF4343] text-[#090E33]' : 'bg-white/5 text-white border border-white/10'}`}>
                  {m.text}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input */}
      <form onSubmit={send} className="bg-[#111827] border border-white/10 border-t border-t-white/5 p-3 flex gap-2" data-testid="messages-input-form">
        <input
          value={text} onChange={e => setText(e.target.value)}
          placeholder={`Message ${cm?.name?.split(' ')[0] || 'your manager'}…`}
          data-testid="messages-input"
          disabled={sending}
          className="flex-1 px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]"
        />
        <button type="submit" disabled={sending || !text.trim()} data-testid="messages-send-btn"
          className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-4 text-sm font-semibold hover:bg-[#E03030] disabled:opacity-50 flex items-center gap-1.5">
          <Send className="w-4 h-4" /> Send
        </button>
      </form>
      <p className="text-[10px] text-[#6B7280] mt-2 font-mono">Delivered instantly · also emailed to {cm?.email || 'jon@freddymedia.com'}</p>
    </div>
  );
}
